import {
  BookkeepingSoftware as BookkeepingSoftwareProto,
  BookkeepingSoftwareType as BookkeepingSoftwareTypeProto,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { match } from 'utils/pattern-match-util'

/**
 * The bookkeeping platforms a client can pick from when granting access.
 * The order here is the order they are displayed in.
 */
export const allBookkeepingPlatforms = ['Xero', 'QuickBooks', 'Other'] as const

export type BookkeepingPlatform = (typeof allBookkeepingPlatforms)[number]

/**
 * The software a client can pick from once they select the `Other` platform.
 */
export const availableBookkeepingSoftwareTypes = [
  'Freshbooks',
  'Puzzle',
  'Sage',
  'Wave',
  'Other',
] as const

export type AvailableBookkeepingSoftwareType =
  (typeof availableBookkeepingSoftwareTypes)[number]

/**
 * The bookkeeping software the client uses for their books.
 */
export type BookkeepingSoftware =
  | { type: 'QuickBooks' }
  | { type: 'Xero' }
  | { type: 'Other'; name: string }

/**
 * Formats the bookkeeping platform for display.
 */
export function formatBookkeepingPlatform(platform: BookkeepingPlatform) {
  return match(platform, {
    Xero: () => 'Xero',
    QuickBooks: () => 'QuickBooks',
    Other: () => 'Other',
  })
}

/**
 * Formats the bookkeeping platform as it is named on its own login page.
 */
export function formatBookkeepingPlatformLoginName(
  platform: BookkeepingPlatform,
) {
  return match(platform, {
    Xero: () => 'Xero',
    QuickBooks: () => 'QuickBooks Online',
    Other: () => 'Other',
  })
}

/**
 * Gets the login URL for the platform, if it has one.
 */
export function getBookkeepingPlatformLoginUrl(platform: BookkeepingPlatform) {
  return match(platform, {
    Xero: () => 'https://login.xero.com/identity/user/login',
    QuickBooks: () => 'https://accounts.intuit.com/app/sign-in',
    Other: () => null,
  })
}

/**
 * Formats the available bookkeeping software for display.
 */
export function formatAvailableBookkeepingSoftware(
  type: AvailableBookkeepingSoftwareType,
) {
  return match(type, {
    Freshbooks: () => 'FreshBooks',
    Puzzle: () => 'Puzzle',
    Sage: () => 'Sage',
    Wave: () => 'Wave',
    Other: () => 'Other',
  })
}

/**
 * Gets the token the API uses to identify the software.
 */
export function getAvailableBookkeepingSoftwareToken(
  type: AvailableBookkeepingSoftwareType,
) {
  return match(type, {
    Freshbooks: () => 'FRESHBOOKS',
    Puzzle: () => 'PUZZLE',
    Sage: () => 'SAGE',
    Wave: () => 'WAVE',
    Other: () => 'OTHER',
  })
}

/**
 * Formats the bookkeeping software for display.
 */
export function formatBookkeepingSoftware(software: BookkeepingSoftware) {
  switch (software.type) {
    case 'QuickBooks':
      return 'QuickBooks'
    case 'Xero':
      return 'Xero'
    case 'Other':
      return software.name
  }
}

/**
 * Maps a {@link BookkeepingSoftware} to its proto representation.
 */
export function mapBookkeepingSoftwareToProto(software: BookkeepingSoftware) {
  const proto = new BookkeepingSoftwareProto()

  switch (software.type) {
    case 'QuickBooks':
      return proto.setType(BookkeepingSoftwareTypeProto.QUICK_BOOKS)
    case 'Xero':
      return proto.setType(BookkeepingSoftwareTypeProto.XERO)
    case 'Other':
      return proto
        .setType(BookkeepingSoftwareTypeProto.OTHER)
        .setOtherName(software.name)
  }
}

/**
 * Maps the proto representation of the bookkeeping software to its model,
 * if one was provided.
 */
export function mapToBookkeepingSoftware(
  software?: BookkeepingSoftwareProto.AsObject,
): BookkeepingSoftware | undefined {
  switch (software?.type) {
    case BookkeepingSoftwareTypeProto.QUICK_BOOKS:
      return { type: 'QuickBooks' }
    case BookkeepingSoftwareTypeProto.XERO:
      return { type: 'Xero' }
    case BookkeepingSoftwareTypeProto.OTHER:
      return { type: 'Other', name: software.otherName }
    default:
      return undefined
  }
}
